import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'lib-request-customer',
  templateUrl: './request-customer.component.html',
  styleUrls: ['./request-customer.component.scss'],
  standalone: false
})
export class RequestCustomerComponent implements OnInit {
  @Output()
  returnToLogin = new EventEmitter<void>();

  protected formBuilder = inject(FormBuilder);
  protected httpClient = inject(HttpClient);
  protected translateService = inject(TranslateService);

  customerForm: FormGroup;
  loader = false;
  alertMessage: { type: string; message: string };

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    this.customerForm = this.formBuilder.group({
      // BE fields — root level
      companyName: this.formBuilder.control('', [Validators.required]),
      companyOrganisationId: this.formBuilder.control('', [Validators.required]),
      deliveryPostalCode: this.formBuilder.control('', [Validators.required]),
      deliveryCity: this.formBuilder.control('', [Validators.required]),
      deliveryCountry: this.formBuilder.control('', [Validators.required]),
      sameAsDelivery: this.formBuilder.control(true),
      billingCompanyName: this.formBuilder.control({ value: '', disabled: true }, [Validators.required]),
      billingStreet: this.formBuilder.control({ value: '', disabled: true }, [Validators.required]),
      billingPostalCode: this.formBuilder.control({ value: '', disabled: true }, [Validators.required]),
      billingCity: this.formBuilder.control({ value: '', disabled: true }, [Validators.required]),
      billingCountry: this.formBuilder.control({ value: '', disabled: true }, [Validators.required]),
      contactPerson: this.formBuilder.control('', [Validators.required]),
      phone: this.formBuilder.control('', [Validators.required]),
      email: this.formBuilder.control('', [Validators.required, Validators.email]),
      invoiceEmail: this.formBuilder.control('', [Validators.required, Validators.email]),
      website: this.formBuilder.control(''),
      // Extra fields — not in BE interface
      extra: this.formBuilder.group({
        companyAddress: this.formBuilder.control('', [Validators.required]),
        additionalInfo: this.formBuilder.control('')
      })
    });

    this.syncBillingFromDelivery();
  }

  protected get sameAsDelivery(): boolean {
    return !!this.customerForm?.get('sameAsDelivery')?.value;
  }

  private syncBillingFromDelivery() {
    const f = this.customerForm;

    f.get('companyName').valueChanges.subscribe(v => {
      if (this.sameAsDelivery) f.get('billingCompanyName').setValue(v, { emitEvent: false });
    });
    f.get('extra.companyAddress').valueChanges.subscribe(v => {
      if (this.sameAsDelivery) f.get('billingStreet').setValue(v, { emitEvent: false });
    });
    f.get('deliveryPostalCode').valueChanges.subscribe(v => {
      if (this.sameAsDelivery) f.get('billingPostalCode').setValue(v, { emitEvent: false });
    });
    f.get('deliveryCity').valueChanges.subscribe(v => {
      if (this.sameAsDelivery) f.get('billingCity').setValue(v, { emitEvent: false });
    });
    f.get('deliveryCountry').valueChanges.subscribe(v => {
      if (this.sameAsDelivery) f.get('billingCountry').setValue(v, { emitEvent: false });
    });
  }

  onSameAsDeliveryChange() {
    const f = this.customerForm;
    const billingFields = ['billingCompanyName', 'billingStreet', 'billingPostalCode', 'billingCity', 'billingCountry'];

    if (this.sameAsDelivery) {
      billingFields.forEach(field => f.get(field).disable());
      f.patchValue({
        billingCompanyName: f.get('companyName').value,
        billingStreet: f.get('extra.companyAddress').value,
        billingPostalCode: f.get('deliveryPostalCode').value,
        billingCity: f.get('deliveryCity').value,
        billingCountry: f.get('deliveryCountry').value
      });
    } else {
      billingFields.forEach(field => f.get(field).enable());
    }
  }

  getFormControl(path: string): AbstractControl {
    return this.customerForm.get(path);
  }

  getButtonText(valid: boolean) {
    return this.translateService.instant(valid ? 'SEND_REQUEST' : 'REQUEST_INVALID_FORM');
  }

  onSubmit() {
    this.alertMessage = null;

    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();
      return;
    }

    this.customerForm.disable();
    this.loader = true;
    this.sendCustomerRequest(this.customerForm.getRawValue());
  }

  sendCustomerRequest(data: any): void {
    const url = this.getRequestUrl();
    this.httpClient.post<any>(url, data).subscribe(
      () => {
        this.customerForm.enable();
        this.customerForm.reset();
        this.initForm();
        this.alertMessage = { type: 'success', message: 'ACCOUNT_REQUEST_SUCCESS' };
        this.loader = false;
      },
      (error: HttpErrorResponse) => {
        const duplicate = error?.error?.errorCode === 'DUPLICATE_ORGANISATION_ID';
        this.alertMessage = {
          type: 'danger',
          message: duplicate ? 'ACCOUNT_REQUEST_DUPLICATE_ORG_NUMBER' : 'ACCOUNT_REQUEST_FAIL'
        };
        this.customerForm.enable();
        this.loader = false;
      }
    );
  }

  protected getRequestUrl(): string {
    return 'json/request-customer';
  }
}
